import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ConnectionStatus, WebSocketService } from '../websocketService';

interface WebSocketContextValue {
  service: WebSocketService;
  status: ConnectionStatus;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const WebSocketProvider: React.FC<{
  url: string;
  children: React.ReactNode;
}> = ({ url, children }) => {
  const wsClientRef = useRef<WebSocketService | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');

  wsClientRef.current ??= new WebSocketService(url);

  useEffect(() => {
    const wsClient = wsClientRef.current;
    if (!wsClient) return;

    wsClient.onStatusChange(setStatus);
    wsClient.connect();

    // Page visibility: reconnect when tab becomes visible if connection is stale
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (wsClient.isConnectionStale()) {
          wsClient.reconnect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wsClient.disconnect();
    };
  }, [url]);

  return (
    <WebSocketContext.Provider value={{ service: wsClientRef.current, status }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
